// ============================================================================
// lib/parent/plan-seats.ts — constantes & helpers PURS pour l'espace parent.
// ============================================================================
// Miroir UI de la fonction SQL `plan_child_seat_limit` (migration 0195) et de
// `PLAN_CHILD_SEATS` dans supabase/functions/_shared/plans.ts — garder synchro.
// Le parent ne consomme PAS de siège : ces plafonds comptent les ENFANTS.
// ============================================================================

/** Plans qu'un parent peut acheter (tutor_monthly exclu). */
export type ParentPlan = 'student_monthly' | 'family_monthly';

export const PARENT_PLANS: readonly ParentPlan[] = ['student_monthly', 'family_monthly'];

/**
 * Product ID store (App Store / Google Play / catalogue interne) d'un plan.
 * Sert au checkout NATIF (RevenueCat cherche le package par product.identifier).
 * Miroir de apps/app/src/lib/checkout/products.ts (CheckoutProductId).
 */
export const PLAN_TO_PRODUCT_ID: Record<ParentPlan, string> = {
  student_monthly: 'jeraisonne_student_monthly',
  family_monthly: 'jeraisonne_family_monthly',
};

/** Plafond de sièges enfants par plan (miroir SQL + edge). */
export const PLAN_CHILD_SEATS: Record<string, number> = {
  student_monthly: 1,
  family_monthly: 3,
  tutor_monthly: 1,
};

export function planChildSeatLimit(plan: string | null | undefined): number {
  if (!plan) return 0;
  return PLAN_CHILD_SEATS[plan] ?? 1;
}

export const PARENT_PLAN_LABELS: Record<ParentPlan, string> = {
  student_monthly: 'Abonnement Élève',
  family_monthly: 'Abonnement Famille',
};

/** Libellé générique d'un plan (inclut tutor pour l'affichage read-only). */
export const PLAN_LABELS: Record<string, string> = {
  student_monthly: 'Abonnement Élève',
  family_monthly: 'Abonnement Famille',
  tutor_monthly: 'Abonnement Prof',
};

export const PARENT_PLAN_PRICE: Record<ParentPlan, string> = {
  student_monthly: '9,99 €',
  family_monthly: '29,99 €',
};

export const PARENT_PLAN_SEATS_LABEL: Record<ParentPlan, string> = {
  student_monthly: '1 enfant',
  family_monthly: "Jusqu'à 3 enfants",
};

export const PARENT_PLAN_DESCRIPTION: Record<ParentPlan, string> = {
  student_monthly: 'Accès complet pour un seul enfant, résiliable à tout moment.',
  family_monthly: "Un seul abonnement pour jusqu'à 3 enfants, facturation centralisée.",
};

/** « 2/3 » sièges utilisés pour un plan donné. */
export function seatsUsedLabel(used: number, plan: string | null | undefined): string {
  return `${used}/${planChildSeatLimit(plan)}`;
}

/** Reste-t-il de la place sur cet abonnement ? */
export function hasFreeSeat(seatsUsed: number, plan: string | null | undefined): boolean {
  return seatsUsed < planChildSeatLimit(plan);
}

/**
 * CTA à afficher sur la carte d'un enfant côté parent :
 *   - `already_covered` : l'enfant a déjà un accès actif ;
 *   - `assignable`      : pas d'accès, mais le parent a un siège libre → attribuer ;
 *   - `needs_plan`      : pas d'accès et aucun siège libre → souscrire un plan.
 */
export type ChildCta = 'already_covered' | 'assignable' | 'needs_plan';

export function deriveChildCta(opts: { hasAccess: boolean; parentHasFreeSeat: boolean }): ChildCta {
  if (opts.hasAccess) return 'already_covered';
  if (opts.parentHasFreeSeat) return 'assignable';
  return 'needs_plan';
}

/** Message d'erreur FR pour les slugs remontés par les RPC/edge fns. */
export const SEAT_ERROR_MESSAGES: Record<string, string> = {
  not_owner: "Tu n'es pas le propriétaire de cet abonnement.",
  not_linked_child: "Cet enfant n'est pas lié à ton compte.",
  seat_limit_reached: 'Cet abonnement a atteint son nombre maximum d’enfants.',
  subscription_inactive: "Cet abonnement n'est pas actif.",
  subscription_not_found: 'Abonnement introuvable.',
  price_not_provisioned: 'Cette formule sera bientôt disponible.',
};

export function seatErrorMessage(slug: string | null | undefined): string {
  if (!slug) return 'Une erreur est survenue. Réessaie.';
  return SEAT_ERROR_MESSAGES[slug] ?? 'Une erreur est survenue. Réessaie.';
}

/** Abonnement (actif, possédé) qui a encore un siège libre — le premier trouvé. */
export function subWithFreeSeat<T extends { plan: string; seats: unknown[] }>(
  subs: readonly T[],
): T | null {
  return subs.find((s) => hasFreeSeat(s.seats.length, s.plan)) ?? null;
}

/** Nombre total de sièges libres sur les abonnements possédés. */
export function freeSeatCount(subs: readonly { plan: string; seats: unknown[] }[]): number {
  return subs.reduce((n, s) => n + Math.max(0, planChildSeatLimit(s.plan) - s.seats.length), 0);
}
